import { useState, type FormEvent } from 'react'
import { safeReturnPath } from '../lib/navigation'
import Logo from '../components/Logo'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import MfaForm from '../components/MfaForm'
import { Button, Field } from '../components/ui'
import { api } from '../lib/api'
import { isConsole } from '../lib/surface'
import { useApi } from '../lib/useApi'
import { useAuth } from '../lib/useAuth'

export default function Login() {
  const { signIn, completeMfa } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(
    new URLSearchParams(location.search).get('error'),
  )
  const [busy, setBusy] = useState(false)
  // Set when the password was right and the account wants its second factor.
  const [mfaToken, setMfaToken] = useState<string | null>(null)

  // Whether this installation has GitHub sign-in at all. Optimistic while
  // loading so the button does not blink out on installs that have it.
  //
  // This has to sit above the two-factor branch below. It used to live under
  // it, next to the markup that uses it, which read better and took the whole
  // page down: showing the second-factor form returns early, so that render
  // ran one hook fewer than the render before it and React threw #300 —
  // "rendered fewer hooks than expected" — leaving a blank screen at the one
  // step every operator must pass through.
  const { data: featureData } = useApi(() => api.features(), [])
  const github = featureData?.github ?? true

  const done = () => {
    const from = (location.state as { from?: string } | null)?.from
    navigate(safeReturnPath(from), { replace: true })
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const challenge = await signIn(email, password)
      if (challenge) {
        setMfaToken(challenge)
        setBusy(false)
        return
      }
      done()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.')
      setBusy(false)
    }
  }

  if (mfaToken) {
    return (
      <div className="auth">
        <div className="auth-card">
          <Logo size={44} className="auth-logo" />
          <MfaForm
            onSubmit={async (code) => {
              await completeMfa(mfaToken, code)
              done()
            }}
            onCancel={() => {
              setMfaToken(null)
              setPassword('')
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="auth">
      <div className="auth-card">
        <Logo size={44} className="auth-logo" />

        {/* Same slot, different job. On the customer dashboard this line is
            the product's pitch, addressed to somebody deciding whether to use
            TalariCloud. On the console it names the place — in the same words
            the console's own chrome uses once you are inside — so an operator
            can see at a glance which of two near-identical pages they are
            looking at, without reading the address bar. */}
        <div className="stack auth-titles" style={{ gap: 4 }}>
          <h1>Sign in</h1>
          <p className="muted">
            {isConsole
              ? 'Operator console'
              : 'Deploy from GitHub to infrastructure in Addis Ababa.'}
          </p>
        </div>

        {error && <div className="alert error">{error}</div>}

        <form className="stack" onSubmit={submit}>
          <Field label="Email" htmlFor="email">
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <label className="check"><input type="checkbox" checked={showPassword} onChange={event => setShowPassword(event.target.checked)} /> Show password</label>
          <Button variant="primary" type="submit" loading={busy} block>
            Sign in
          </Button>
        </form>

        {/* Not on the console. An operator holds the keys to every account on
            the platform, and that door should not depend on a third party's
            availability or on whoever controls the GitHub organisation. A
            password this installation issued and a second factor it enrolled
            are both things you hold. It is also one fewer thing on a page
            that should be a form and nothing else.

            Nobody loses access by this: the account is the same either way,
            and an operator who has only ever used GitHub sets a password
            through the reset flow. */}
        {!isConsole &&
          (github ? (
            <>
              <div className="divider">or</div>
              <a className="btn block" href={api.githubStartUrl()} onClick={() => sessionStorage.setItem('talaricloud.returnTo', safeReturnPath((location.state as {from?: string} | null)?.from))}>
                Continue with GitHub
              </a>
            </>
          ) : (
            <p className="tiny faint" style={{ textAlign: 'center' }}>
              GitHub sign-in is not configured on this installation, so email
              it is.
            </p>
          ))}

        {/* Open signup is shown only for installations configured for it.
            Invitation-only installations offer the operator request flow. */}
        {!isConsole && featureData?.invite_only === false && (
          <p className="tiny muted" style={{ textAlign: 'center' }}>
            No account?{' '}
            <Link to="/signup" style={{ color: 'var(--brand-text)' }}>
              Create one
            </Link>
          </p>
        )}
        {!isConsole && featureData?.invite_only === true && <p className="tiny muted" style={{ textAlign: 'center' }}>Need an account? <Link to="/request-invite" state={location.state}>Request an invitation</Link></p>}
      </div>
    </div>
  )
}

